import { useQuery } from "@tanstack/react-query";
import { UserPaymentTrancheApi, userPaymentsApi } from "api";
import { PAYMENT_STATUSES_LABELS } from "consts";
import { PaziaLoader, PaziaButton } from "components/atoms";
import { formatDate, toMoneyFormat } from "utils";
import { QUERIES_KEYS } from "queries";
import React from "react";
import { MdCheck } from "react-icons/md";
import { useParams } from "react-router-dom";
import { EditTranchePriceModal } from "./EditTranchePriceModal";
import { EditTrancheStatusModal } from "./EditTrancheStatusModal";
import { PostponePaymentModal } from "./PostponePaymentModal";
import { AddTrancheModal } from "./AddTrancheModal";
import { GeneratePaymentLinkModal } from "./GeneratePaymentLinkModal";
import { StudentPaymentsTrancheRow, TableDefinition, TableHeader } from "./StudentPaymentsTrancheRow";
import { StudentPaymentsTrancheCard } from "./StudentPaymentsTrancheCard";
import { EditTrancheModal } from "./EditTrancheModal";

export const StudentPaymentsTab = () => {
  const { studentId, courseId } = useParams();
  const [postponePayment, setPostponePayment] = React.useState<UserPaymentTrancheApi | undefined>(undefined);
  const [editTrancheStatus, setEditTrancheStatus] = React.useState<UserPaymentTrancheApi | undefined>(undefined);
  const [editTranchePrice, setEditTranchePrice] = React.useState<UserPaymentTrancheApi | undefined>(undefined);
  const [editTranche, setEditTranche] = React.useState<UserPaymentTrancheApi | undefined>(undefined);
  const [showAddTrancheModal, setShowAddTrancheModal] = React.useState(false);
  const [showGeneratePaymentLinkModal, setShowGeneratePaymentLinkModal] = React.useState(false);
  const [selectedTrancheForPayment, setSelectedTrancheForPayment] = React.useState<UserPaymentTrancheApi | undefined>(
    undefined,
  );

  const { isLoading, data, refetch } = useQuery({
    queryKey: QUERIES_KEYS.USER_PAYMENTS(studentId!, courseId),
    queryFn: () => userPaymentsApi.fetch(Number(studentId), Number(courseId)),
    enabled: !!studentId,
  });

  const handleGeneratePaymentLink = (tranche: UserPaymentTrancheApi) => {
    setSelectedTrancheForPayment(tranche);
    setShowGeneratePaymentLinkModal(true);
  };

  const handleEditTranche = (tranche: UserPaymentTrancheApi) => {
    setEditTranche(tranche);
  };

  if (isLoading) return <PaziaLoader position="center" />;
  if (!data?.data) return null;

  const { leftToPay, tranches, payments } = data.data;

  return (
    <div className="mt-4">
      <div className="mb-6 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Transze płatności</h3>
        <PaziaButton
          btnType="button"
          type="primary"
          onClick={() => setShowAddTrancheModal(true)}
          title="Dodaj transzę"
        />
      </div>

      <div className="space-y-2 md:hidden">
        {tranches?.map((tranche) => (
          <StudentPaymentsTrancheCard
            key={tranche.courseTrancheId}
            tranche={tranche}
            onPostponePayment={() => setPostponePayment(tranche)}
            setEditStatusPayment={() => setEditTrancheStatus(tranche)}
            setEditPricePayment={() => setEditTranchePrice(tranche)}
            onGeneratePaymentLink={() => handleGeneratePaymentLink(tranche)}
            onEditTranche={() => handleEditTranche(tranche)}
          />
        ))}
        {leftToPay.amount === 0 ? (
          <div className="flex items-center justify-center gap-2 rounded-xl bg-green-50 p-3 font-semibold text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <MdCheck size={20} />
            Opłacone w całości
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-xl bg-blue-50 p-3 dark:bg-blue-900/20">
            <span className="font-semibold text-blue-700 dark:text-blue-400">Pozostało do opłacenia</span>
            <span className="font-bold text-blue-700 dark:text-blue-400">
              {toMoneyFormat(Number(leftToPay.amount))} PLN
            </span>
          </div>
        )}
      </div>

      <div className="mt-4 hidden overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:block dark:border-slate-700 dark:bg-slate-900">
        <table className="w-full">
          <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
            <tr>
              <TableHeader>ID</TableHeader>
              <TableHeader>Nazwa</TableHeader>
              <TableHeader>Należy opłacić</TableHeader>
              <TableHeader>Kwota</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Akcje</TableHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {tranches?.map((tranche) => (
              <StudentPaymentsTrancheRow
                key={tranche.courseTrancheId}
                tranche={tranche}
                onPostponePayment={() => setPostponePayment(tranche)}
                setEditStatusPayment={() => setEditTrancheStatus(tranche)}
                setEditPricePayment={() => setEditTranchePrice(tranche)}
                onGeneratePaymentLink={() => handleGeneratePaymentLink(tranche)}
                onEditTranche={() => handleEditTranche(tranche)}
              />
            ))}
            {leftToPay.amount === 0 ? (
              <tr className="bg-green-50 dark:bg-green-900/20">
                <TableDefinition> </TableDefinition>
                <TableDefinition> </TableDefinition>
                <TableDefinition>
                  <div className="flex items-center gap-2 font-semibold text-green-700 dark:text-green-400">
                    <MdCheck size={20} />
                    Opłacone w całości
                  </div>
                </TableDefinition>
                <TableDefinition> </TableDefinition>
                <TableDefinition> </TableDefinition>
                <TableDefinition> </TableDefinition>
              </tr>
            ) : (
              <tr className="bg-blue-50 dark:bg-blue-900/20">
                <TableDefinition> </TableDefinition>
                <TableDefinition> </TableDefinition>
                <TableDefinition>
                  <div className="font-semibold text-blue-700 dark:text-blue-400">Pozostało do opłacenia</div>
                </TableDefinition>
                <TableDefinition>
                  <div className="font-bold text-blue-700 dark:text-blue-400">
                    {toMoneyFormat(Number(leftToPay.amount))} PLN
                  </div>
                </TableDefinition>
                <TableDefinition> </TableDefinition>
                <TableDefinition> </TableDefinition>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {payments.length > 0 && (
        <div className="mt-6">
          <div className="mb-4 font-semibold text-slate-900 dark:text-slate-100">Dokonane płatności</div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                <tr>
                  <TableHeader>Utworzono</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Kwota</TableHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {payments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="transition-colors duration-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <TableDefinition>
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {formatDate(new Date(payment.createdDate), true)}
                      </div>
                    </TableDefinition>
                    <TableDefinition>
                      <div className="text-slate-700 dark:text-slate-300">
                        {PAYMENT_STATUSES_LABELS[payment.status]}
                      </div>
                    </TableDefinition>
                    <TableDefinition>
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{payment.amount} PLN</div>
                    </TableDefinition>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <PostponePaymentModal
        onCloseRequest={() => {
          setPostponePayment(undefined);
          refetch();
        }}
        tranche={postponePayment}
        courseId={Number(courseId)}
        userId={Number(studentId)}
      />
      <EditTrancheStatusModal
        onCloseRequest={() => {
          setEditTrancheStatus(undefined);
          refetch();
        }}
        tranche={editTrancheStatus}
        courseId={Number(courseId)}
        userId={Number(studentId)}
      />
      <EditTranchePriceModal
        onCloseRequest={() => {
          setEditTranchePrice(undefined);
          refetch();
        }}
        tranche={editTranchePrice}
        courseId={Number(courseId)}
        userId={Number(studentId)}
      />
      <AddTrancheModal
        onCloseRequest={() => {
          setShowAddTrancheModal(false);
          refetch();
        }}
        isOpen={showAddTrancheModal}
        courseId={Number(courseId)}
        userId={Number(studentId)}
      />
      <GeneratePaymentLinkModal
        onCloseRequest={() => {
          setShowGeneratePaymentLinkModal(false);
          setSelectedTrancheForPayment(undefined);
        }}
        isOpen={showGeneratePaymentLinkModal}
        courseId={Number(courseId)}
        userId={Number(studentId)}
        tranches={tranches}
        selectedTranche={selectedTrancheForPayment}
      />
      <EditTrancheModal
        onCloseRequest={() => {
          setEditTranche(undefined);
          refetch();
        }}
        tranche={editTranche}
        courseId={Number(courseId)}
        userId={Number(studentId)}
      />
    </div>
  );
};
